/**
 * QuietMenu — the nav-bar "Quiet" control (Ken 2026-10-18 · Batch 2 2026-10-19).
 *
 * Setting (Batch 1): a small "Quiet" label; click → a popup anchored BESIDE the
 * label with By: [Person] [Matter], a one-line rule-builder, faint quick-picks of
 * previously-set rules, and the "Currently active:" chips (click to turn off).
 * Hover (when chimes are set) → a peek of the active chips.
 *
 * Responding (Batch 2): unacknowledged hits show as a COUNT pip on the label and
 * a "For your attention:" list in the popup (below the watch-list, above the sound
 * settings); clicking a hit drills into its matter (and the triggering email) and
 * marks it seen → the pip decrements. A soft CHIME plays once when a new hit arrives,
 * if the user has armed sound (a one-time gesture — browsers block audio until then).
 */

import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '../auth'
import ChimeChooser from './ChimeChooser'
import {
  listQuietRules, resolveQuietRule, toggleQuietRule,
  listQuietHits, markQuietHitSeen, markAllQuietHitsSeen,
  chimeSoundAudioUrl,
  type QuietRule,
} from '../api'

// Hover tooltip for a set rule: a person's full name + firm address (or just the
// address, off-roster), a matter's full name. Falls back to the short chip label.
function ruleTip(r: QuietRule): string {
  if (r.kind === 'person') return [r.person_name, r.person_email].filter(Boolean).join(' · ') || r.label
  return r.matter_name || r.label
}

type Kind = 'person' | 'matter'
type AskState = { prompt: string; options: { label: string; value: string | number; sub?: string }[] }

export default function QuietMenu() {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const auth = useAuth()
  const wrapRef = useRef<HTMLDivElement>(null)
  const [open, setOpen] = useState(false)
  const [hovering, setHovering] = useState(false)
  const [kind, setKind] = useState<Kind | null>(null)
  const [text, setText] = useState('')
  const [ask, setAsk] = useState<AskState | null>(null)

  const rulesQ = useQuery({ queryKey: ['quiet-rules'], queryFn: listQuietRules, staleTime: 30_000 })
  const active = rulesQ.data?.active ?? []
  const inactive = rulesQ.data?.inactive ?? []
  const hasActive = active.length > 0

  // Hits poll — drives the pip + the Caught list. Only polls when any rule is
  // set (nothing to catch otherwise). 15s is snappy enough; cheap query.
  const hitsQ = useQuery({
    queryKey: ['quiet-hits'], queryFn: listQuietHits,
    refetchInterval: 15_000, staleTime: 8_000, enabled: hasActive,
  })
  const unseen = hitsQ.data?.unseen_count ?? 0
  const hits = hitsQ.data?.hits ?? []

  // ── Sound (armed once, by a user gesture) ───────────────────────────────
  // Chime is ON by default — browsers still need one user gesture (any click) to
  // unlock audio, which the first-gesture unlock below handles; the popup toggle is
  // now just an optional mute. (Only an explicit mute, stored '0', turns it off.)
  const [soundOn, setSoundOn] = useState<boolean>(() => {
    try { return localStorage.getItem('quiet-sound-armed') !== '0' } catch { return true }
  })
  const unlockedRef = useRef(false)
  // ONE persistent audio element, unlocked inside a user gesture and reused for every
  // chime. Reusing a gesture-unlocked element is far more reliable than `new Audio()`
  // each time — a fresh element created in a timer/poll callback gets blocked by
  // Chrome's autoplay policy even after the page has activation.
  const chimeRef = useRef<HTMLAudioElement | null>(null)
  function getChime(): HTMLAudioElement {
    if (!chimeRef.current) { chimeRef.current = new Audio(); chimeRef.current.preload = 'auto' }
    return chimeRef.current
  }
  // The user's chosen chime (shadow_ui.chime_id → /me). null = built-in default.
  function chimeUrl(): string {
    const cid = auth.status === 'authenticated' ? (auth.user.chime_id ?? null) : null
    return cid ? chimeSoundAudioUrl(cid) : '/quiet-chime.wav'
  }
  function playChime() {
    try {
      const a = getChime()
      a.src = chimeUrl()
      a.currentTime = 0
      a.volume = 0.5
      void a.play().catch(() => {})
    } catch { /* ignore */ }
  }
  // Unlock the persistent element from WITHIN a user gesture (play it muted). After
  // this, programmatic .play() on the same element is allowed for the page's life.
  function unlockAudio() {
    if (unlockedRef.current) return
    try {
      const a = getChime()
      a.src = chimeUrl()
      a.volume = 0
      void a.play().then(() => { a.pause(); a.currentTime = 0; a.volume = 0.5; unlockedRef.current = true }).catch(() => {})
    } catch { /* ignore */ }
  }
  // Belt-and-suspenders: also unlock on the first click/keypress anywhere.
  useEffect(() => {
    if (!soundOn || unlockedRef.current) return
    const h = () => unlockAudio()
    document.addEventListener('pointerdown', h, { once: true })
    document.addEventListener('keydown', h, { once: true })
    return () => {
      document.removeEventListener('pointerdown', h)
      document.removeEventListener('keydown', h)
    }
  }, [soundOn])
  function toggleSound() {
    const next = !soundOn
    setSoundOn(next)
    try { localStorage.setItem('quiet-sound-armed', next ? '1' : '0') } catch { /* ignore */ }
    if (next) unlockAudio()  // this click is the gesture: unlock SILENTLY (no preview — use ▶ to hear a sound)
  }
  // Chime on a genuinely-new hit (count climbed) — never on first load.
  const prevUnseen = useRef<number | null>(null)
  useEffect(() => {
    const n = hitsQ.data?.unseen_count ?? 0
    if (prevUnseen.current === null) { prevUnseen.current = n; return }
    if (n > prevUnseen.current && soundOn) playChime()
    prevUnseen.current = n
  }, [hitsQ.data?.unseen_count, soundOn])

  const invalidateRules = () => qc.invalidateQueries({ queryKey: ['quiet-rules'] })
  const invalidateHits = () => qc.invalidateQueries({ queryKey: ['quiet-hits'] })
  // No shared onSuccess — the two call sites behave differently: a typed phrase that
  // resolves cleanly clears the box, but picking from the "which one?" list must LEAVE
  // the list up so you can add the other matches too (Ken 2026-10-21).
  const resolveM = useMutation({ mutationFn: resolveQuietRule })
  const toggleM = useMutation({ mutationFn: toggleQuietRule, onSuccess: invalidateRules })
  const seenM = useMutation({ mutationFn: markQuietHitSeen, onSuccess: invalidateHits })
  const seenAllM = useMutation({ mutationFn: markAllQuietHitsSeen, onSuccess: invalidateHits })

  function closeAll() { setOpen(false); setKind(null); setText(''); setAsk(null) }

  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) closeAll()
    }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') closeAll() }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  async function submitPhrase(e: FormEvent) {
    e.preventDefault()
    if (!kind || !text.trim()) return
    try {
      const res = await resolveM.mutateAsync({ kind, text: text.trim() })
      if (res.status === 'created') { setText(''); setAsk(null); invalidateRules() }
      else setAsk({ prompt: res.prompt, options: res.options })
    } catch { /* leave the box as-is so the user can retry */ }
  }
  // Pick one option from the "which one?" list. We DON'T clear the list — adding a
  // match just flips it to "on" (derived from the live active rules below), so you can
  // keep tapping to add the others. Already-active matches are shown ticked + disabled.
  async function pickOption(v: string | number) {
    if (!kind) return
    try {
      const res = await resolveM.mutateAsync(
        kind === 'person' ? { kind, chosenEmail: String(v) } : { kind, chosenMatterId: Number(v) })
      if (res.status === 'created') invalidateRules()
    } catch { /* ignore — nothing added */ }
  }
  // The active rule for an ask-option, if any (pre-existing or just added). Lets the
  // option render as "on" (grey) and be clicked off right there in the list.
  function activeRuleFor(v: string | number) {
    return active.find((r) =>
      kind === 'person' ? r.kind === 'person' && r.person_email === String(v)
                        : r.kind === 'matter' && r.matter_id === Number(v))
  }
  function drillIn(h: { id: number; matter_id: number | null; email_artifact_id: number | null }) {
    seenM.mutate(h.id)
    if (h.matter_id) {
      const q = new URLSearchParams({ matter: String(h.matter_id) })
      if (h.email_artifact_id) q.set('artifact', String(h.email_artifact_id))
      navigate(`/workspace?${q.toString()}`)
    }
    closeAll()
  }

  const inactiveOfKind = inactive.filter((r) => r.kind === kind)

  return (
    <div
      className="relative"
      ref={wrapRef}
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
    >
      <button
        onClick={() => { unlockAudio(); return open ? closeAll() : setOpen(true) }}
        // Styled as a peer nav tab (Ken 2026-10-22) now that Quiet lives in the
        // nav row. Uses --me-font-nav so it matches the other nav page labels
        // (the other tabs moved off --me-font-chrome onto --me-font-nav; Ken 2026-10-27).
        className="me-nav-tab"
        style={{ fontFamily: 'var(--me-font-nav)' }}
        title={unseen > 0 ? `${unseen} caught` : undefined}
      >
        Quiet
        {unseen > 0 ? (
          <span
            className="inline-flex min-w-[15px] items-center justify-center rounded-full px-1 text-[10px] font-semibold leading-none text-white"
            style={{ background: 'var(--me-new-pip)', height: 15 }}
          >
            {unseen}
          </span>
        ) : hasActive ? (
          <span className="inline-block h-1.5 w-1.5 rounded-full" style={{ background: 'var(--me-nav-color)' }} />
        ) : null}
      </button>

      {/* Hover peek — active chips, only when chimes are set and popup is closed. */}
      {hovering && !open && hasActive && (
        <div className="absolute left-0 top-full z-40 mt-1 flex max-w-[260px] flex-wrap gap-1 rounded me-card p-2 shadow-lg">
          {active.map((r) => (
            <span key={r.id} title={ruleTip(r)} className="rounded-full border me-border px-2 py-0.5 text-[11px] me-text">{r.label}</span>
          ))}
        </div>
      )}

      {/* Popup — anchored beside the label. */}
      {open && (
        <div className="absolute left-0 top-full z-50 mt-1 w-[262px] rounded me-card p-2.5 shadow-lg">
          <div className="mb-1 text-[11px] me-text-mute">Only interrupt my Quiet for email:</div>
          <div className="flex items-center gap-1.5">
            <KindBtn on={kind === 'person'} onClick={() => { setKind('person'); setAsk(null); setText('') }}>from this Person</KindBtn>
            <KindBtn on={kind === 'matter'} onClick={() => { setKind('matter'); setAsk(null); setText('') }}>about this Matter</KindBtn>
          </div>

          {kind && (
            <div className="mt-2">
              {inactiveOfKind.length > 0 && (
                <div className="mb-1.5 flex flex-wrap gap-1">
                  {inactiveOfKind.map((r) => (
                    <button key={r.id} onClick={() => toggleM.mutate(r.id)} title="Turn back on"
                      className="rounded-full border me-border px-2 py-0.5 text-[11px] me-text-mute opacity-60 hover:opacity-100">
                      {r.label}
                    </button>
                  ))}
                </div>
              )}
              <form onSubmit={submitPhrase}>
                <input
                  // Remount per kind (key) so a fresh, un-flagged element is used each time
                  // you switch Person↔Matter — stops LastPass's icon from sticking once it
                  // has attached to the element. The person placeholder deliberately avoids
                  // the word "email" (a LastPass field-detection trigger); the hint lives in
                  // a sibling line below instead. (Ken 2026-10-21.)
                  key={kind}
                  name="quiet-rule-phrase"
                  autoFocus
                  value={text}
                  onChange={(e) => { setText(e.target.value); setAsk(null) }}
                  placeholder={kind === 'person' ? 'a name' : 'your natural keywords'}
                  className="me-input w-full text-[12px]"
                  disabled={resolveM.isPending}
                  autoComplete="off"
                  data-lpignore="true"
                  data-1p-ignore
                  data-form-type="other"
                />
              </form>
              {kind === 'person' && (
                <div className="mt-1 text-[10px] me-text-mute">A first name from your firm, or a full email address.</div>
              )}
              {ask && (
                <div className="mt-1.5">
                  <div className="mb-1 text-[11px] me-text-mute">{ask.prompt}</div>
                  {ask.options.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {ask.options.map((o) => {
                        // Picked/active options go grey (no checkmark) and, like the chips
                        // below, strike through on hover — click one to turn it back off
                        // right here (Ken 2026-10-21). Un-picked ones add on click.
                        const onRule = activeRuleFor(o.value)
                        return (
                          <button
                            key={String(o.value)}
                            onClick={() => { onRule ? toggleM.mutate(onRule.id) : pickOption(o.value) }}
                            disabled={resolveM.isPending}
                            title={onRule ? ruleTip(onRule) : o.sub}
                            className={
                              'rounded-full border me-border px-2 py-0.5 text-[11px] ' +
                              (onRule
                                ? 'me-text-mute opacity-60 hover:line-through'
                                : 'me-text hover:bg-[color:var(--me-selected)]')
                            }
                          >
                            {o.label}
                          </button>
                        )
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {hasActive && (
            <div className="mt-2.5">
              <div className="mb-1 text-[11px] me-text-mute">Currently monitoring for:</div>
              <div className="flex flex-wrap gap-1">
                {active.map((r) => (
                  <button key={r.id} onClick={() => toggleM.mutate(r.id)} title={ruleTip(r)}
                    className="rounded-full border me-border px-2 py-0.5 text-[11px] me-text hover:line-through">
                    {r.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* For your attention — what actually fired since you last looked. Sits below
              the watch-list and above sound settings so, when the pip/chime pulls you in,
              the thing that tripped it is right there. Hits can accumulate between visits,
              so this is a list; click one to open its email in the matter (clears it from
              here, decrements the pip). Dynamic — only shown when something is waiting.
              (Ken 2026-10-21 — was the "Caught:" list at the top of the popup.) */}
          {unseen > 0 && (
            <div className="mt-2.5 border-t me-border pt-1.5">
              <div className="mb-1 flex items-center justify-between">
                <span className="text-[11px] font-medium me-text">For your attention:</span>
                <button onClick={() => seenAllM.mutate()} className="text-[10px] me-text-mute hover:me-text" title="Clear all">
                  clear all
                </button>
              </div>
              <div className="flex flex-col gap-0.5">
                {hits.map((h) => (
                  <button
                    key={h.id}
                    onClick={() => drillIn(h)}
                    className="flex items-center justify-between gap-2 rounded px-1.5 py-1 text-left text-[12px] me-text hover:bg-[color:var(--me-selected)]"
                    title="Open the email in its matter"
                  >
                    <span className="truncate font-medium">{h.rule_label || 'match'}</span>
                    <span className="shrink-0 truncate text-[11px] me-text-mute">{firstName(h.sender)}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Chime sound — the picker also lives here, where you actually think
              about Quiet (Ken 2026-10-21). ▶ demos a sound (and arms audio). */}
          <div className="mt-2.5 border-t me-border pt-1.5">
            <div className="mb-1 flex items-center justify-between">
              <span className="text-[11px] me-text-mute">Choose a sound:</span>
              <button onClick={toggleSound} className="text-[11px] me-text hover:me-text"
                title={soundOn ? 'Sound on — click for pip-only (no sound)' : 'Pip only — click to turn the sound on'}>
                {soundOn ? 'on' : 'Pip only'}
              </button>
            </div>
            <ChimeChooser />
          </div>
        </div>
      )}
    </div>
  )
}

function firstName(s: string): string {
  const t = (s || '').trim()
  if (!t) return ''
  if (t.includes('@') && !t.includes(' ')) return t.split('@')[0]
  // "Last, First" (common Gmail display form) → the given name.
  if (t.includes(',')) {
    const after = t.split(',')[1]?.trim().split(/\s+/)[0]
    if (after) return after
  }
  return t.split(/\s+/)[0]
}

function KindBtn({ on, onClick, children }: { on: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button
      onClick={onClick}
      data-selected={on ? 'true' : undefined}
      // Text colour via per-tenant var (default = var(--me-text), unchanged) so
      // KEN can style the Quiet popup buttons apart from body text (Ken 2026-10-22).
      className="rounded-full border me-border px-2.5 py-0.5 text-[11px] data-[selected]:bg-[color:var(--me-selected)]"
      style={{ color: 'var(--me-quiet-btn-text, var(--me-text))' }}
    >
      {children}
    </button>
  )
}
